import React, { useMemo } from "react";
import { IMisdeliveryInvestigation } from "../models";

interface MisdeliveryInvestigationTemplateProps {
  values?: IMisdeliveryInvestigation;
  osdNumber: number;
}

const MisdeliveryInvestigationTemplate: React.FC<
  MisdeliveryInvestigationTemplateProps
> = ({ values = {}, osdNumber }) => {
  const memoizedValues = useMemo(() => values, [values]);

  const hasValue = (value?: string | number | null) =>
    value !== undefined && value !== null && value !== "";

  const yesNo = (value?: string) => {
    if (value === "Y") return "Yes";
    if (value === "N") return "No";
    return undefined;
  };

  const getReasonLabel = (value?: string | number | null) => {
    switch (value) {
      case "CC":
        return "Consignee Corrected";
      case "CK":
        return "Consignee is keeping or has inventoried the freight";
      case "SC":
        return "Shipper Corrected";
      case "OR":
        return "Other Reason";
      default:
        return value;
    }
  };

  const getDeliveryStatusLabel = (value?: string | number | null) => {
    switch (value) {
      case "DS":
        return "Delivered Short (DS)";
      case "AS":
        return "All Short (AS)";
      case "DC":
        return "Delivered Clear (DC)";
      default:
        return value;
    }
  };

  const renderSectionHeader = (title: string) => (
    <div style={styles.sectionHeader}>{title}</div>
  );

  const renderSection = (title: string, value?: string | number | null) => (
    <div style={styles.sectionContainer}>
      <div style={styles.sectionTitle}>{title}</div>
      <div style={styles.valueBox}>{value ?? ""}</div>
    </div>
  );

  const renderOptionSection = (
    title: string,
    value?: string | number | null,
    secondaryValue?: string | number | null,
  ) => (
    <div style={styles.sectionContainer}>
      <div style={styles.sectionTitle}>{title}</div>
      <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
        <div style={styles.optionBox}>{value ?? ""}</div>
        {hasValue(secondaryValue) && (
          <div style={styles.secondaryBox}>{secondaryValue}</div>
        )}
      </div>
    </div>
  );

  return (
    <div id="misdelivery-investigation-template">
      <div style={styles.header}>
        Misdelivery Investigation
        <span style={styles.badge}>#{osdNumber}</span>
      </div>

      <div style={styles.container}>
        {renderSectionHeader("Pickup Recovery")}
        {renderSection(
          "Was the misdelivered freight pick up completed?",
          yesNo(memoizedValues.wasPickUpCompleted),
        )}
        {memoizedValues.wasPickUpCompleted === "Y" && (
          <>
            {renderOptionSection(
              "If the new PRO was attached to the BOL ?",
              yesNo(memoizedValues.wasNewProAttachedToBol),
            )}
            {memoizedValues.wasNewProAttachedToBol === "N" &&
              renderSection("New PRO #", memoizedValues.newProNumber)}
          </>
        )}
        {memoizedValues.wasPickUpCompleted === "N" &&
          renderOptionSection(
            "Was pick up rescheduled ?",
            yesNo(memoizedValues.wasPickUpRescheduled),
          )}
        {memoizedValues.wasPickUpRescheduled === "Y" &&
          renderSection("New PU #", memoizedValues.pickupNumber)}
        {memoizedValues.wasPickUpRescheduled === "N" &&
          renderSection(
            "Reason pickup cannot be completed",
            getReasonLabel(memoizedValues.reasonUnableToPickUpCode),
          )}
        {memoizedValues.wasPickUpRescheduled === "N" &&
          memoizedValues.reasonUnableToPickUpCode === "OR" &&
          renderSection(
            "Additional comments",
            memoizedValues.otherReasonComments,
          )}
        {renderSectionHeader("Freight Location Verification")}
        {renderSection(
          "Is the Misdelivered freight in the OS&D Bay ?",
          yesNo(memoizedValues.isFreightInOsdBay),
        )}
        {memoizedValues.isFreightInOsdBay === "N" && (
          <>
            {renderOptionSection(
              "Did the freight move on an OD400",
              yesNo(memoizedValues.didFreightMoveOnOd400),
            )}
            {memoizedValues.didFreightMoveOnOd400 === "Y" && (
              <>
                {renderOptionSection(
                  "Was an OD400 created by the dock ?",
                  yesNo(memoizedValues.wasOd400CreatedByDock),
                )}
                {memoizedValues.wasOd400CreatedByDock === "Y" &&
                  renderSection("OD PRO#", memoizedValues.od400ProNumber)}
              </>
            )}
            {memoizedValues.didFreightMoveOnOd400 === "N" && (
              <>
                {renderSection(
                  "All short the MISDELV pickup PRO#",
                  memoizedValues.misdeliveryPickupProNumber,
                )}
                {renderSection("AS Exception #", memoizedValues.asExceptionNumber)}
              </>
            )}
          </>
        )}
        {memoizedValues.didFreightMoveOnOd400 !== "N" && (
          <>
            {renderOptionSection(
              "When the misdelivered freight is picked up, will the original misdelivered PRO become short ?",
              yesNo(memoizedValues.willOriginalProBecomeShort),
            )}
            {renderSectionHeader("Address & Billing Verification")}
            {renderOptionSection(
              "Does the address in AS400 match the BOL?",
              yesNo(memoizedValues.doesAs400AddressMatchBol),
            )}
            {memoizedValues.doesAs400AddressMatchBol !== "N" && (
              <>
                {renderOptionSection(
                  "Does the BOL show SLC ?",
                  yesNo(memoizedValues.doesBolShowSlc),
                )}
                {renderOptionSection(
                  "Does the shipment have any label?",
                  yesNo(memoizedValues.isFreightLabeled),
                )}
                {memoizedValues.isFreightLabeled === "Y" &&
                  renderOptionSection(
                    "Does the freight label show the misdelivery pick-up address?",
                    yesNo(memoizedValues.isFreightLabeledShowingMsdAddress),
                  )}
              </>
            )}

            {memoizedValues.isFreightLabeledShowingMsdAddress === "N" && (
              <>
                {renderSectionHeader("Freight Label Address")}
                {renderSection("Address 1", memoizedValues.misdeliveryAddress1)}
                {renderSection("Address 2", memoizedValues.misdeliveryAddress2)}
                {renderSection("Zip", memoizedValues.misdeliveryZip)}
                {renderSection("City", memoizedValues.misdeliveryCity)}
                {renderSection("State", memoizedValues.misdeliveryState)}
              </>
            )}

            {memoizedValues.doesAs400AddressMatchBol !== "N" && (
              <>
                {renderSectionHeader("MISDELV Shipment Tracking Identification")}
                {renderOptionSection(
                  "Does the misdelivered shipment have any labels?",
                  yesNo(memoizedValues.doesMsdShipmentHaveLabels),
                )}
                {memoizedValues.doesMsdShipmentHaveLabels === "Y" && (
                  <>
                    {renderOptionSection(
                      "Is this a cross shipment?",
                      yesNo(memoizedValues.isCrossShipment),
                    )}
                    {renderOptionSection(
                      "Does the freight have a SAIA PRO sticker?",
                      yesNo(memoizedValues.doesFreightHaveSaiaProSticker),
                    )}
                    {memoizedValues.doesFreightHaveSaiaProSticker === "N" && (
                      <>
                        {renderOptionSection(
                          "Is the PRO in transit ?",
                          yesNo(memoizedValues.isProInTransit),
                        )}
                      </>
                    )}
                    {memoizedValues.doesFreightHaveSaiaProSticker === "Y" &&
                      renderSection(
                        "Verify the delivery status on the SAIA PRO # ?",
                        getDeliveryStatusLabel(memoizedValues.deliveryStatusOnSaiaPro),
                      )}
                  </>
                )}
                {memoizedValues.doesMsdShipmentHaveLabels === "N" &&
                  renderSection("NB Exception #", memoizedValues.nbException)}
              </>
            )}
            {memoizedValues.deliveryStatusOnSaiaPro === "DS" &&
              renderSection(
                "List suffix bill for the applies to PRO#",
                memoizedValues.suffixBillForAppliesToPro,
              )}
            {memoizedValues.deliveryStatusOnSaiaPro === "AS" &&
              renderSection(
                "List the applied to PRO # shipment is moving on",
                memoizedValues.appliedToProShipmentMovingOn,
              )}
            {memoizedValues.deliveryStatusOnSaiaPro === "DC" &&
              renderSection(
                "OV Exception #",
                memoizedValues.ovExceptionNumber,
              )}
          </>
        )}

        {renderSectionHeader("Freight Handling")}
        {renderSection("Driver: who delivered the shipment", memoizedValues.driverName)}
        {renderSection(
          "Dockworker: who loaded the shipment onto P&D trailer",
          memoizedValues.dockWorkerName,
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    width: "100%",
    background: "#f5f7fb",
    padding: "10px 15px",
    boxSizing: "border-box" as const,
    fontFamily: "calibri, sans-serif",
  },
  header: {
    width: "100%",
    background: "#f5f7fb",
    padding: "10px 15px",
    fontSize: "18px",
    fontWeight: 700,
    textTransform: "uppercase" as const,
    borderBottom: "1px solid #d5d7db",
    boxSizing: "border-box" as const,
  },
  badge: {
    background: "#f5f5f5",
    border: "1px solid #d4d4d4",
    padding: "5px 15px",
    borderRadius: "20px",
    fontSize: "14px",
    fontWeight: 700,
    marginLeft: "10px",
    display: "inline-block" as const,
  },
  sectionContainer: {
    margin: "12px 0",
  },
  sectionHeader: {
    margin: "18px 0 10px",
    fontWeight: 700,
    fontSize: "15px",
    color: "#111827",
    textTransform: "uppercase" as const,
    letterSpacing: "0.02em",
    borderBottom: "1px solid #d5d7db",
    paddingBottom: "4px",
  },
  sectionTitle: {
    margin: "0 0 8px",
    fontWeight: 600,
    color: "#1f2937",
  },
  valueBox: {
    background: "#ffffff",
    padding: "10px",
    border: "1px solid #b5b6b9",
    wordWrap: "break-word" as const,
    whiteSpace: "pre-wrap" as const,
  },
  optionBox: {
    width: "15vw",
    minWidth: "120px",
    background: "#ffffff",
    padding: "10px",
    border: "1px solid #b5b6b9",
    display: "inline-block" as const,
    verticalAlign: "top" as const,
  },
  secondaryBox: {
    width: "75%",
    background: "#ffffff",
    padding: "10px",
    border: "1px solid #b5b6b9",
    display: "inline-block" as const,
    wordWrap: "break-word" as const,
    whiteSpace: "pre-wrap" as const,
  },
};

export default MisdeliveryInvestigationTemplate;
